import { deepEqual } from "@/utils/deepEqual"
import { useEffect, useMemo, useRef, useState } from "react"
import { CustomLayerInterface, LayerSpecification, Map } from "maplibre-gl"
import { useMapContext } from "@/components/maps/mapLibre/MapContext"
import assert from "@/utils/assert"

// Omiting property from a union type, see
// https://github.com/microsoft/TypeScript/issues/39556#issuecomment-656925230
type OptionalId<T> = T extends { id: string }
  ? Omit<T, "id"> & { id?: string }
  : T
type OptionalSource<T> = T extends { source: string }
  ? Omit<T, "source"> & { source?: string }
  : T

export type LayerProps = (
  | OptionalSource<OptionalId<LayerSpecification>>
  | CustomLayerInterface
) & {
  /** If set, the layer will be inserted before the specified layer */
  beforeId?: string
}

function updateLayer(
  map: Map,
  id: string,
  props: LayerProps,
  prevProps: LayerProps,
) {
  assert(props.id === prevProps.id, "layer id changed")
  assert(props.type === prevProps.type, "layer type changed")

  if (props.type === "custom" || prevProps.type === "custom") {
    return
  }

  // @ts-ignore filter does not exist in some Layer types
  const { layout = {}, paint = {}, filter, minzoom, maxzoom, beforeId } = props

  if (beforeId !== prevProps.beforeId) {
    map.moveLayer(id, beforeId)
  }
  if (layout !== prevProps.layout) {
    const prevLayout = prevProps.layout || {}
    for (const key in layout) {
      // @ts-ignore
      if (!deepEqual(layout[key], prevLayout[key])) {
        // @ts-ignore
        map.setLayoutProperty(id, key, layout[key])
      }
    }
    for (const key in prevLayout) {
      if (!Object.prototype.hasOwnProperty.call(layout, key)) {
        map.setLayoutProperty(id, key, undefined)
      }
    }
  }
  if (paint !== prevProps.paint) {
    const prevPaint = prevProps.paint || {}
    for (const key in paint) {
      // @ts-ignore
      if (!deepEqual(paint[key], prevPaint[key])) {
        // @ts-ignore
        map.setPaintProperty(id, key, paint[key])
      }
    }
    for (const key in prevPaint) {
      if (!Object.prototype.hasOwnProperty.call(paint, key)) {
        map.setPaintProperty(id, key, undefined)
      }
    }
  }

  // @ts-ignore filter does not exist in some Layer types
  if (!deepEqual(filter, prevProps.filter)) {
    map.setFilter(id, filter)
  }
  if (minzoom !== prevProps.minzoom || maxzoom !== prevProps.maxzoom) {
    // @ts-ignore
    map.setLayerZoomRange(id, minzoom, maxzoom)
  }
}

function createLayer(map: Map, id: string, props: LayerProps) {
  // @ts-ignore
  if (
    map.style &&
    map.style._loaded &&
    (!("source" in props) || map.getSource(props.source!))
  ) {
    const options: LayerProps = { ...props, id }
    delete options.beforeId

    // @ts-ignore
    map.addLayer(options, props.beforeId)
  }
}

let layerCounter = 0

export function Layer(props: LayerProps) {
  const map = useMapContext()?.getMapInstance()
  if (!map) return null
  const propsRef = useRef(props)
  const [, setStyleLoaded] = useState(0)

  const id = useMemo(() => props.id || `jsx-layer-${layerCounter++}`, [])

  useEffect(() => {
    if (map) {
      const forceUpdate = () => setStyleLoaded((version) => version + 1)
      map.on("styledata", forceUpdate)
      forceUpdate()

      return () => {
        map.off("styledata", forceUpdate)
        // @ts-ignore
        if (map.style && map.style._loaded && map.getLayer(id)) {
          map.removeLayer(id)
        }
      }
    }
    return undefined
  }, [map])

  // @ts-ignore
  const layer = map && map.style && map.getLayer(id)
  if (layer) {
    try {
      updateLayer(map, id, props, propsRef.current)
    } catch (error) {
      console.warn(error)
    }
  } else {
    createLayer(map, id, props)
  }

  // Store last rendered props
  propsRef.current = props

  return null
}
